import { OrderAction } from 'Store/Order/actions';

export interface IOrder {
  isFetchingOrder: boolean;
  isFetchOrderFailed: boolean;
  isFetchingPayment: boolean;
  isFetchPaymentFailed: boolean;
  isFetchingDiscount: boolean;
  isDiscountFailed: boolean;
  isTermsApproved: boolean;
  orderRequest: Julius.Website.OrderRequestViewModel;
  selectedDeliveryCode: string;
  selectedPaymentCode: string;
  selectedCustomerInfo: Julius.Website.CustomerInfoViewModel;
  selectedPackages: Julius.Website.PackageRequestViewModel[];
  selectedSkanetrafiken: Julius.Website.TravelRouteViewModel[];
  selectedVouchers: Julius.Website.VoucherViewModel[];
  packages: Julius.Website.PackageViewModel[];
  deliveryMethods: Julius.Website.DeliveryMethodViewModel[];
  paymentMethods: Julius.Website.PaymentMethodViewModel[];
  skanetrafiken: Julius.Website.SkanetrafikenItem;
  orderSeats: Julius.Website.OrderSeatViewModel[];
  orderVouchers: Julius.Website.OrderItemViewModel[];
  orderItems: Julius.Website.OrderItemViewModel[];
  orderID: number;
  orderBarcode: string;
  orderTotalPrice: number;
  paymentStatus: string;
  paymentUrl: string;
  orderExpireDate: Date;
  isRetry: boolean;
  isValid: boolean;
};

const initialOrder: IOrder = {
  isFetchingOrder: false,
  isFetchOrderFailed: false,
  isFetchingPayment: false,
  isFetchPaymentFailed: false,
  isFetchingDiscount: false,
  isDiscountFailed: false,
  isTermsApproved: false,
  orderRequest: {
    performanceID: -1,
    seatRequests: [],
    voucherRequests: [],
    isScatteredSeats: false,
    isManualReservation: false,
  },
  selectedDeliveryCode: null,
  selectedPaymentCode: null,
  selectedCustomerInfo: null,
  selectedPackages: [],
  selectedSkanetrafiken: [],
  selectedVouchers: [],
  packages: [],
  deliveryMethods: [],
  paymentMethods: [],
  skanetrafiken: null,
  orderSeats: [],
  orderItems: [],
  orderVouchers: [],
  orderID: null,
  orderBarcode: null,
  orderTotalPrice: null,
  paymentStatus: null,
  paymentUrl: null,
  orderExpireDate: null,
  isRetry: false,
  isValid: true,
};

function order(state: IOrder = initialOrder, action: OrderAction): IOrder {
  switch (action.type) {

    case 'SET_ORDER':
      const newOrder = action.order;

      const defaultPaymentCode = newOrder.paymentMethods && 
        newOrder.paymentMethods.length ? newOrder.paymentMethods[0].name : null;

      return {
        ...state,
        isFetchingOrder: false,
        isFetchOrderFailed: false,
        isRetry: newOrder.isOrderRetry,
        orderID: newOrder.orderID,
        orderSeats: newOrder.orderSeats,
        orderVouchers: newOrder.orderVouchers,
        orderItems: newOrder.orderItems,
        orderBarcode: newOrder.orderBarcode,
        orderTotalPrice: newOrder.orderTotalPrice,
        orderRequest: initialOrder.orderRequest,
        paymentStatus: newOrder.paymentStatus,
        selectedDeliveryCode: newOrder.selectedDeliveryCode,
        selectedPaymentCode: defaultPaymentCode,
        selectedCustomerInfo: newOrder.selectedCustomerInfo,
        selectedPackages: newOrder.selectedPackages,
        selectedSkanetrafiken: newOrder.selectedSkanetrafiken,
        selectedVouchers: newOrder.selectedVouchers,
        packages: newOrder.packages,
        deliveryMethods: newOrder.deliveryMethods,
        paymentMethods: newOrder.paymentMethods,
        skanetrafiken: newOrder.skanetrafiken,
        isValid: newOrder.isValid,
      };

    case 'SET_DISCOUNT_RESULT':
      return {
        ...state,
        orderSeats: action.order.orderSeats,
        orderTotalPrice: action.order.orderTotalPrice,
        selectedVouchers: action.order.selectedVouchers,
        isFetchingDiscount: false,
        isDiscountFailed: false,
      };

    case'SET_ORDER_CONFIRMATION':
      const { orderConfirmation } = action;
      
      return {
        ...state,
        isFetchingOrder: false,
        isFetchOrderFailed: false,
        orderItems: orderConfirmation.orderItems,
        selectedDeliveryCode: orderConfirmation.selectedDeliveryCode,
        selectedCustomerInfo: orderConfirmation.selectedCustomerInfo,
        paymentStatus: orderConfirmation.paymentStatus,
        orderID: orderConfirmation.orderID,
        orderBarcode: orderConfirmation.orderBarcode,
        orderTotalPrice: orderConfirmation.orderTotalPrice,
      };
      
    case 'SET_PAYMENT_RESULT':
      const { paymentResult } = action;

      return {
        ...state,
        paymentStatus: paymentResult.paymentStatus,
        paymentUrl: paymentResult.paymentUrl,
        orderID: paymentResult.orderID,
        isFetchingPayment: false,
        isFetchPaymentFailed: false,
      };

    case 'SET_IS_MANUAL_RESERVATION':
      return {
        ...state,
        orderRequest: {
          ...state.orderRequest,
          isManualReservation: action.isManualReservation,
        },
      };

    case 'SET_PERFORMANCE_ID':
      return {
        ...state,
        orderRequest: {
          ...state.orderRequest,
          performanceID: action.performanceID,
        },
      };

    case 'SET_ORDER_REQUEST':
      return {
        ...state,
        orderRequest: action.request ? action.request : initialOrder.orderRequest,
      };

    case 'SET_VOUCHER_REQUEST':
      const { voucherRequest } = action;

      return {
        ...state,
        orderRequest: {
          ...state.orderRequest,
          voucherRequests: state.orderRequest.voucherRequests.concat(voucherRequest),
        },
      };

    case 'SET_SEAT_REQUESTS':
      return {
        ...state,
        orderRequest: {
          ...state.orderRequest,
          seatRequests: action.seatRequests,
        },
      };

    case 'SET_IS_SCATTERED_SEATS':
      return {
        ...state,
        orderRequest: {
          ...state.orderRequest,
          isScatteredSeats: action.isScatteredSeats,
        },
      };

    case 'SET_ORDER_EXPIRE_DATE':
      return {
        ...state,
        orderExpireDate: action.expireDate,
      };

    case 'SET_SELECTED_PACKAGES':
      return {
        ...state,
        selectedPackages: action.packages,
      };

    case 'ADD_SKANETRAFIKEN_TRAVEL':
      const skanetrafikenAdded = state.selectedSkanetrafiken ?
        state.selectedSkanetrafiken.concat(action.travel) : [ action.travel ];

      return {
        ...state,
        selectedSkanetrafiken: skanetrafikenAdded,
      };

    case 'REMOVE_SKANETRAFIKEN_TRAVEL':
    const skanetrafikenRemoved = state.selectedSkanetrafiken ?
      state.selectedSkanetrafiken.filter(s => 
        !(s.seatReference === action.travel.seatReference && 
          s.direction === action.travel.direction)) : [];

      return {
        ...state,
        selectedSkanetrafiken: skanetrafikenRemoved,
      };

    case 'SET_SELECTED_CUSTOMER_INFO':
      return {
        ...state,
        selectedCustomerInfo: action.customerInfo,
      };

    case 'SET_CUSTOMER_TERMS_APPROVAL':
      return {
        ...state,
        isTermsApproved: action.isApproved,
      };

    case 'SET_SELECTED_DELIVERY_CODE':
      return {
        ...state,
        selectedDeliveryCode: action.deliveryCode,
      };

    case 'SET_SELECTED_PAYMENT_NAME':
     return {
       ...state,
       selectedPaymentCode: action.paymentName,
     };

    case 'SET_IS_FETCHING_ORDER':
      return {
        ...state,
        isFetchingOrder: true,
        isFetchOrderFailed: false,
      };

    case 'SET_FETCH_ORDER_FAILED':
      return {
        ...state,
        isFetchingOrder: false,
        isFetchOrderFailed: true,
      };

    case 'SET_IS_FETCHING_PAYMENT':
      return {
        ...state,
        isFetchingPayment: true,
        isFetchPaymentFailed: false,
      };

    case 'SET_FETCH_PAYMENT_FAILED':
      return {
        ...state,
        isFetchingPayment: false,
        isFetchPaymentFailed: action.isFailed,
      };

    case 'SET_IS_FETCHING_DISCOUNT':
      return {
        ...state,
        isFetchingDiscount: true,
        isDiscountFailed: false,
      }

    case 'SET_DISCOUNT_FAILED':
      {
        const orderFields = action.order ? {
          orderSeats: action.order.orderSeats,
          orderTotalPrice: action.order.orderTotalPrice,
          selectedVouchers: action.order.selectedVouchers,
        } : {}; 
      return {
        ...state,
        isFetchingDiscount: false,
        isDiscountFailed: true,
        ...orderFields,        
      };
    }
    case 'SET_IS_RETRY':
      return {
        ...state,
        isRetry: true,
      };

    case 'CLEAR_ORDER':
      return initialOrder;
      
    default:
      return state;
  }
}

export default order;


// WEBPACK FOOTER //
// ./src/store/Order/reducer.tsx